import React, { useMemo, useState } from "react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

type BillingType = "opd" | "ipd" | "lab" | "pharmacy";

type PendingPayment = {
  id: number;
  patientName: string;
  type: BillingType;
  reference: string;
  amount: number;
  dueDate: string;
};

const BillingList: React.FC = () => {
  const [activeType, setActiveType] = useState<BillingType | "all">("all");
  const [searchText, setSearchText] = useState("");

  const pendingPayments: PendingPayment[] = [
    {
      id: 1,
      patientName: "Aarav Sharma",
      type: "opd",
      reference: "OPD-1023",
      amount: 850,
      dueDate: "2025-02-18",
    },
    {
      id: 2,
      patientName: "Meera Patel",
      type: "ipd",
      reference: "IPD-4501",
      amount: 12450,
      dueDate: "2025-02-16",
    },
    {
      id: 3,
      patientName: "Rohan Das",
      type: "lab",
      reference: "LAB-7781",
      amount: 1350,
      dueDate: "2025-02-19",
    },
    {
      id: 4,
      patientName: "Sneha Iyer",
      type: "pharmacy",
      reference: "PH-3922",
      amount: 560,
      dueDate: "2025-02-19",
    },
  ];

  const filteredPayments = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();
    return pendingPayments.filter((payment) => {
      const matchesType =
        activeType === "all" ? true : payment.type === activeType;
      const matchesSearch =
        !normalizedSearch ||
        payment.patientName.toLowerCase().includes(normalizedSearch) ||
        payment.reference.toLowerCase().includes(normalizedSearch);
      return matchesType && matchesSearch;
    });
  }, [activeType, searchText, pendingPayments]);

  const totals = useMemo(() => {
    const base = {
      all: 0,
      opd: 0,
      ipd: 0,
      lab: 0,
      pharmacy: 0,
    };
    return pendingPayments.reduce((acc, payment) => {
      acc.all += payment.amount;
      acc[payment.type] += payment.amount;
      return acc;
    }, base);
  }, [pendingPayments]);

  const typeLabels: Record<BillingType | "all", string> = {
    all: "All",
    opd: "OPD Visits",
    ipd: "IPD Visits",
    lab: "Lab Orders",
    pharmacy: "Pharmacy",
  };

  const formatType = (type: BillingType) => {
    switch (type) {
      case "opd":
        return "OPD Visit";
      case "ipd":
        return "IPD Visit";
      case "lab":
        return "Lab Order";
      case "pharmacy":
        return "Pharmacy";
      default:
        return type;
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            Billing
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Pending payments across OPD, IPD, Lab, and Pharmacy.
          </p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm font-medium text-gray-600">Total Pending</p>
          <p className="mt-2 text-2xl font-bold text-gray-900">₹{totals.all}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm font-medium text-gray-600">OPD Pending</p>
          <p className="mt-2 text-2xl font-bold text-blue-600">₹{totals.opd}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm font-medium text-gray-600">IPD Pending</p>
          <p className="mt-2 text-2xl font-bold text-indigo-600">₹{totals.ipd}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm font-medium text-gray-600">Lab Pending</p>
          <p className="mt-2 text-2xl font-bold text-amber-600">₹{totals.lab}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-sm font-medium text-gray-600">Pharmacy Pending</p>
          <p className="mt-2 text-2xl font-bold text-emerald-600">
            ₹{totals.pharmacy}
          </p>
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2">
          {(["all", "opd", "ipd", "lab", "pharmacy"] as const).map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => setActiveType(type)}
              className={`rounded-full px-4 py-2 text-sm font-semibold ${
                activeType === type
                  ? "bg-primary text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {typeLabels[type]}
            </button>
          ))}
        </div>
        <div className="relative w-full max-w-sm">
          <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          <input
            type="text"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder="Search by patient or reference"
            className="w-full rounded-md border border-gray-300 pl-10 pr-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      </div>

      <div className="flow-root mt-6">
        <div className="overflow-x-auto -mx-4 -my-2 sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >
                      Patient
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Type
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Reference
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Due Date
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Amount
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {filteredPayments.length > 0 ? (
                    filteredPayments.map((payment) => (
                      <tr key={payment.id}>
                        <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                          {payment.patientName}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          {formatType(payment.type)}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          {payment.reference}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          {payment.dueDate}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-900 font-semibold">
                          ₹{payment.amount}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          <button
                            type="button"
                            className="rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-hover"
                          >
                            Collect Payment
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={6}
                        className="py-10 text-center text-sm text-gray-500"
                      >
                        No pending payments found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingList;
